import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Home, Plus, Trash2, Edit, LogOut, Eye, Send, 
  FileText, Tag, Save, X, Share2, Mail
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogAdmin = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [user, setUser] = useState(null);

  // Formulaire article
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    image_url: '',
    category_id: '',
    tags: [],
    status: 'draft',
    comments_enabled: true
  });

  const [newTag, setNewTag] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [showCategoryModal, setShowCategoryModal] = useState(false);

  useEffect(() => {
    verifyAuth();
  }, []);

  useEffect(() => {
    if (user) {
      fetchArticles();
      fetchCategories();
    }
  }, [user]);

  const verifyAuth = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      toast.error('Session expirée');
      localStorage.removeItem('admin_token');
      navigate('/login');
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchArticles = async () => {
    try {
      const response = await axios.get(`${API}/blog/articles/all`, getAuthHeaders());
      setArticles(response.data.articles);
    } catch (error) {
      console.error('Erreur chargement articles:', error);
      toast.error('Erreur lors du chargement des articles');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/blog/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Erreur chargement catégories:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/login');
  };

  const openModal = (article = null) => {
    if (article) {
      setSelectedArticle(article);
      setFormData({
        title: article.title,
        excerpt: article.excerpt || '',
        content: article.content,
        image_url: article.image_url || '',
        category_id: article.category_id || '',
        tags: article.tags || [],
        status: article.status,
        comments_enabled: article.comments_enabled
      });
    } else {
      setSelectedArticle(null);
      setFormData({
        title: '',
        excerpt: '',
        content: '',
        image_url: '',
        category_id: '',
        tags: [],
        status: 'draft',
        comments_enabled: true
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedArticle(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (selectedArticle) {
        await axios.patch(`${API}/blog/articles/${selectedArticle.id}`, formData, getAuthHeaders());
        toast.success('Article mis à jour');
      } else {
        await axios.post(`${API}/blog/articles`, formData, getAuthHeaders());
        toast.success('Article créé');
      }
      
      closeModal();
      fetchArticles();
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (articleId, title) => {
    if (!window.confirm(`Supprimer l'article "${title}" ?`)) return;

    try {
      await axios.delete(`${API}/blog/articles/${articleId}`, getAuthHeaders());
      toast.success('Article supprimé');
      fetchArticles();
    } catch (error) {
      console.error('Erreur suppression:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleShareFacebook = async (articleId, title) => {
    if (!window.confirm(`Partager "${title}" sur Facebook ?`)) return;

    try {
      const response = await axios.post(`${API}/blog/articles/${articleId}/share-facebook`, {}, getAuthHeaders());
      toast.success('Article partagé sur Facebook !');
      window.open(response.data.facebook_url, '_blank');
    } catch (error) {
      console.error('Erreur partage:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du partage sur Facebook');
    }
  };

  // Créer une campagne d'emailing depuis un article
  const handleCreateCampaign = async (articleId, title) => {
    if (!window.confirm(`Créer une campagne email depuis l'article "${title}" ?\n\nUne campagne pré-remplie avec le contenu de l'article et un CTA sera créée.`)) return;

    try {
      const response = await axios.post(
        `${API}/blog/articles/${articleId}/create-campaign`, 
        {
          utm_source: 'blog',
          utm_medium: 'email'
        },
        getAuthHeaders()
      );
      
      toast.success('Campagne créée avec succès !', { duration: 4000 });
      
      // Rediriger vers la page des campagnes pour éditer
      navigate(`/admin/campaigns?edit=${response.data.campaign_id}`);
    } catch (error) {
      console.error('Erreur création campagne:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création de la campagne');
    }
  };

  const addTag = () => {
    if (newTag && !formData.tags.includes(newTag)) {
      setFormData({ ...formData, tags: [...formData.tags, newTag] });
      setNewTag('');
    }
  };

  const removeTag = (tag) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });
  };

  const handleCreateCategory = async () => {
    if (!newCategory.trim()) return;

    try {
      await axios.post(`${API}/blog/categories?name=${encodeURIComponent(newCategory)}`, {}, getAuthHeaders());
      toast.success('Catégorie créée');
      setNewCategory('');
      setShowCategoryModal(false);
      fetchCategories();
    } catch (error) {
      console.error('Erreur création catégorie:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F5F5F5' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: '#335C2E' }}></div>
          <p className="mt-4" style={{ color: '#666666' }}>Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#F5F5F5' }}>
      <header className="shadow-sm" style={{ background: '#FFFFFF' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold" style={{ color: '#335C2E' }}>
              <FileText className="inline mr-2" size={28} />
              Admin Blog
            </h1>
            <div className="flex items-center gap-3">
              <button
                onClick={() => window.open('/#blog', '_blank')}
                className="flex items-center gap-2 px-4 py-2 rounded hover:opacity-90"
                style={{ background: '#2196F3', color: '#FFFFFF' }}
                title="Ouvre dans un nouvel onglet"
              >
                <Eye size={18} />
                Voir le blog
              </button>
              <button
                onClick={() => navigate('/admin')}
                className="flex items-center gap-2 px-4 py-2 rounded hover:opacity-90"
                style={{ background: '#335C2E', color: '#FFFFFF' }}
              >
                <Home size={18} />
                Retour Admin
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 rounded hover:opacity-90"
                style={{ background: '#DC3545', color: '#FFFFFF' }}
              >
                <LogOut size={18} />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => openModal()}
            className="flex items-center gap-2 px-6 py-3 rounded-lg hover:opacity-90 font-medium"
            style={{ background: '#335C2E', color: '#FFFFFF' }}
          >
            <Plus size={20} />
            Nouvel article
          </button>
          <button
            onClick={() => setShowCategoryModal(true)}
            className="flex items-center gap-2 px-6 py-3 rounded-lg hover:opacity-90"
            style={{ background: '#C7A027', color: '#FFFFFF' }}
          >
            <Tag size={20} />
            Gérer les catégories
          </button>
        </div>

        <div className="rounded-lg shadow overflow-hidden" style={{ background: '#FFFFFF' }}>
          <table className="w-full">
            <thead style={{ background: '#F5F5F5' }}>
              <tr>
                <th className="text-left p-3 font-semibold">Titre</th>
                <th className="text-center p-3 font-semibold">Statut</th>
                <th className="text-center p-3 font-semibold">Vues</th>
                <th className="text-left p-3 font-semibold">Date</th>
                <th className="text-center p-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {articles.map((article) => (
                <tr key={article.id} className="border-b hover:bg-gray-50">
                  <td className="p-3">
                    <div className="font-medium" style={{ color: '#333333' }}>{article.title}</div>
                    <div className="text-xs" style={{ color: '#999999' }}>{article.excerpt?.substring(0, 80)}...</div>
                  </td>
                  <td className="p-3 text-center">
                    <span
                      className="px-2 py-1 rounded text-xs font-medium"
                      style={{
                        background: article.status === 'published' ? '#E8F5E9' : '#F5F5F5',
                        color: article.status === 'published' ? '#2E7D32' : '#666666'
                      }}
                    >
                      {article.status === 'published' ? 'Publié' : 'Brouillon'}
                    </span>
                  </td>
                  <td className="p-3 text-center">{article.views}</td>
                  <td className="p-3 text-sm" style={{ color: '#666666' }}>
                    {new Date(article.created_at).toLocaleDateString('fr-CA')}
                  </td>
                  <td className="p-3">
                    <div className="flex gap-1 justify-center">
                      <button
                        onClick={() => openModal(article)}
                        className="p-2 rounded hover:bg-gray-200"
                        title="Modifier"
                      >
                        <Edit size={16} style={{ color: '#335C2E' }} />
                      </button>
                      {article.status === 'published' && (
                        <>
                          <button
                            onClick={() => handleShareFacebook(article.id, article.title)}
                            className="p-2 rounded hover:bg-blue-100"
                            title="Partager sur Facebook"
                          >
                            <Share2 size={16} style={{ color: '#1877F2' }} />
                          </button>
                          <button
                            onClick={() => handleCreateCampaign(article.id, article.title)}
                            className="p-2 rounded hover:bg-orange-100"
                            title="Créer une campagne email"
                          >
                            <Mail size={16} style={{ color: '#E65100' }} />
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => handleDelete(article.id, article.title)}
                        className="p-2 rounded hover:bg-red-100"
                        title="Supprimer"
                      >
                        <Trash2 size={16} style={{ color: '#DC3545' }} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {articles.length === 0 && (
            <div className="text-center py-12" style={{ color: '#999999' }}>
              Aucun article pour le moment
            </div>
          )}
        </div>
      </div>

      {/* Modal Article */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold" style={{ color: '#335C2E' }}>
                {selectedArticle ? 'Modifier l\'article' : 'Nouvel article'}
              </h2>
              <button onClick={closeModal} className="p-2 hover:bg-gray-100 rounded">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Titre *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    className="w-full p-3 border rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Résumé</label>
                  <textarea
                    value={formData.excerpt}
                    onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                    rows="2"
                    className="w-full p-3 border rounded-lg"
                    placeholder="Court résumé de l'article..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">URL de l'image</label>
                  <input
                    type="url"
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    className="w-full p-3 border rounded-lg"
                    placeholder="https://..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Contenu HTML *</label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    required
                    rows="12"
                    className="w-full p-3 border rounded-lg font-mono text-sm"
                    placeholder="<p>Contenu de l'article...</p>"
                  />
                  
                  {/* Image Uploader */}
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm font-medium mb-2">Ajouter une image</p>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        
                        const uploadFormData = new FormData();
                        uploadFormData.append('file', file);
                        
                        try {
                          const response = await axios.post(
                            `${API}/upload/image`,
                            uploadFormData,
                            {
                              headers: {
                                Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
                                'Content-Type': 'multipart/form-data'
                              }
                            }
                          );
                          
                          if (response.data.success) {
                            const imageUrl = `${BACKEND_URL}${response.data.url}`;
                            const imgTag = `<img src="${imageUrl}" alt="Image article" style="max-width: 100%; height: auto; margin: 20px 0;" />`;
                            setFormData({
                              ...formData,
                              content: formData.content + '\n' + imgTag
                            });
                            toast.success('Image ajoutée à l\'article');
                          }
                        } catch (error) {
                          toast.error('Erreur lors de l\'upload: ' + (error.response?.data?.detail || error.message));
                        }
                      }}
                      className="text-sm w-full"
                    />
                    <p className="text-xs text-gray-500 mt-1">L'image sera insérée dans le contenu HTML</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Catégorie</label>
                    <select
                      value={formData.category_id}
                      onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                      className="w-full p-3 border rounded-lg"
                    >
                      <option value="">Aucune</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Statut</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="w-full p-3 border rounded-lg"
                    >
                      <option value="draft">Brouillon</option>
                      <option value="published">Publié</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Tags</label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {formData.tags.map((tag, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 rounded-full text-sm flex items-center gap-1"
                        style={{ background: '#E8F5E9', color: '#2E7D32' }}
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeTag(tag)}
                          className="hover:opacity-70"
                        >
                          <X size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                      className="flex-1 p-2 border rounded"
                      placeholder="Ajouter un tag..."
                    />
                    <button
                      type="button"
                      onClick={addTag}
                      className="px-4 py-2 rounded"
                      style={{ background: '#335C2E', color: '#FFFFFF' }}
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.comments_enabled}
                    onChange={(e) => setFormData({ ...formData, comments_enabled: e.target.checked })}
                    id="comments-enabled"
                    className="w-4 h-4"
                  />
                  <label htmlFor="comments-enabled" className="text-sm">Activer les commentaires</label>
                </div>
              </div>

              <div className="flex gap-3 justify-end mt-6">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 rounded border"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-2 px-6 py-2 rounded hover:opacity-90"
                  style={{ background: '#335C2E', color: '#FFFFFF' }}
                >
                  <Save size={18} />
                  {selectedArticle ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Catégories */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold" style={{ color: '#335C2E' }}>Catégories</h2>
              <button onClick={() => setShowCategoryModal(false)} className="p-2 hover:bg-gray-100 rounded">
                <X size={20} />
              </button>
            </div>

            <div className="p-6">
              <div className="mb-4">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleCreateCategory()}
                    className="flex-1 p-2 border rounded"
                    placeholder="Nouvelle catégorie..."
                  />
                  <button
                    onClick={handleCreateCategory}
                    className="px-4 py-2 rounded"
                    style={{ background: '#335C2E', color: '#FFFFFF' }}
                  >
                    <Plus size={18} />
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                {categories.map((cat) => (
                  <div key={cat.id} className="flex items-center justify-between p-2 rounded hover:bg-gray-50">
                    <span>{cat.name}</span>
                    <button
                      onClick={async () => {
                        if (window.confirm(`Supprimer la catégorie "${cat.name}" ?`)) {
                          try {
                            await axios.delete(`${API}/blog/categories/${cat.id}`, getAuthHeaders());
                            toast.success('Catégorie supprimée');
                            fetchCategories();
                          } catch (error) {
                            toast.error('Erreur lors de la suppression');
                          }
                        }
                      }}
                      className="p-1 rounded hover:bg-red-100"
                    >
                      <Trash2 size={14} style={{ color: '#DC3545' }} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogAdmin;
